import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";
import { getTokenContext } from "utils/tokenUtils";
import {
  getAirports,
  getNonProfits,
  getUsers,
} from "../../services/graphqlService";
import {
  derivePilotStatus,
  CARGO_FORM_NEEDED,
  CARGO_FORM_SOURCED,
  normalizeCargoStatusForForm,
} from "../../../missionSourcingStatus";
import { normalizeMissionRecord } from "../../../missionLocationUtils";
import MissionDocumentsField from "components/MissionDocumentsField";
import MissionBannerField from "components/MissionBannerField";
import { useMissionDocuments } from "hooks/useMissionDocuments";
import {
  cropImageToBannerFile,
  parseMissionImageRef,
} from "utils/missionDocuments";

function getPostedByFromToken(idToken) {
  if (!idToken) return "";
  const ctx = getTokenContext(idToken);
  const orgTypes = ["NonProfit", "Org", "nonprofit", "org"];
  if (orgTypes.includes(ctx?.entityType) && ctx?.entityId)
    return String(ctx.entityId);
  return "";
}

const STEPS = [
  "Basic Info",
  "Routing & Legs",
  "Pilot Requirements",
  "Impact & POC",
  "Cargo Items",
  "Passengers",
];

const MISSION_CATEGORY_OPTIONS = [
  { value: "Medical", label: "Medical" },
  { value: "Animal Rescue", label: "Animal Rescue" },
  { value: "Disaster Relief", label: "Disaster Relief" },
  { value: "Veteran Support", label: "Veteran Support" },
  { value: "Conservation", label: "Conservation" },
  { value: "Aviation Experience", label: "Aviation Experience" },
  { value: "other", label: "Others" },
];

const MISSION_TYPE_PUBLIC = "Public";
const MISSION_TYPE_INTERNAL = "Internal";

const normalizeMissionVisibility = (value) =>
  String(value || "").trim() === MISSION_TYPE_PUBLIC
    ? MISSION_TYPE_PUBLIC
    : MISSION_TYPE_INTERNAL;

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
].map((s) => ({ value: s, label: s }));

function parseCityState(str) {
  if (!str || typeof str !== "string") return { city: "", state: "" };
  const parts = str.split(",").map((p) => p.trim());
  if (parts.length >= 2) {
    return {
      city: parts[0] || "",
      state: parts[parts.length - 1] || "",
    };
  }
  return { city: parts[0] || str, state: "" };
}

/**
 * API may return location as flat departureCity/arrivalCity, combined string,
 * or nested { city, state } on departure/arrival (GraphQL locationInput shape).
 */
function parseLocationFields(flatCity, flatState, combinedOrObject) {
  if (flatCity != null && String(flatCity).trim() !== "") {
    return {
      city: String(flatCity).trim(),
      state: flatState != null ? String(flatState).trim() : "",
    };
  }
  if (flatState != null && String(flatState).trim() !== "") {
    return { city: "", state: String(flatState).trim() };
  }
  const v = combinedOrObject;
  if (v && typeof v === "object" && !Array.isArray(v)) {
    return {
      city: v.city != null ? String(v.city).trim() : "",
      state: v.state != null ? String(v.state).trim() : "",
    };
  }
  if (typeof v === "string") {
    return parseCityState(v);
  }
  return { city: "", state: "" };
}

const defaultCargoItem = () => ({
  description: "",
  count: "",
  weightLbs: "",
  sourcedWeightLbs: "",
  isSourced: "",
  isDeliveredToAirport: "",
  linkedRequestId: "",
  providedByOrgId: "",
});

const defaultPassengerItem = () => ({
  fullName: "",
  age: "",
  weightLbs: "",
  specialNeeds: "",
  isConfirmed: "",
  linkedRequestId: "",
});

const defaultLeg = () => ({
  legId: "",
  departureAirportId: "",
  arrivalAirportId: "",
  pilotId: "",
});

const defaultPilotRequirements = () => ({
  commercialRating: "",
  flightHourMinimums: "",
  instrumentFlightRating: "",
  privatePilotsLicense: "",
});

const PILOT_REQUIREMENT_OPTIONS = [
  { key: "commercialRating", label: "Commercial Rating" },
  { key: "instrumentFlightRating", label: "Instrument Flight Rating" },
  { key: "privatePilotsLicense", label: "Private Pilot's License" },
];

const FLIGHT_HOUR_MINIMUM_OPTIONS = [
  { value: "None", label: "None" },
  { value: "100", label: "100" },
  { value: "150", label: "150" },
  { value: "200", label: "200" },
  { value: "250", label: "250" },
  { value: "500", label: "500" },
  { value: "1000", label: "1000" },
  { value: "1500+", label: "1500+" },
];

function normalizeUsersList(data) {
  if (Array.isArray(data)) return data;
  if (data?.ProfileList) return data.ProfileList;
  if (data?.UsersList) return data.UsersList;
  if (data?.items) return data.items;
  if (data?.data) return data.data;
  return [];
}

// Match Autocomplete input height with SoftInput / other form fields
const autocompleteInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    maxHeight: 40,
    alignItems: "center",
    "& .MuiOutlinedInput-input": {
      py: 0,
    },
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

function normalizeAirportsList(data) {
  if (Array.isArray(data)) return data;
  if (data?.AirportsList) return data.AirportsList;
  if (data?.items) return data.items;
  if (data?.data) return data.data;
  return [];
}

function normalizeNonprofitsList(data) {
  if (Array.isArray(data)) return data;
  if (data?.NonProfitsList) return data.NonProfitsList;
  if (data?.items) return data.items;
  if (data?.data) return data.data;
  return [];
}

function MissionForm({
  onSubmit,
  onCancel,
  initialData = null,
  loading = false,
  idToken = null,
}) {
  const seed = initialData ? normalizeMissionRecord(initialData) : null;
  const [activeStep, setActiveStep] = useState(0);
  const [airports, setAirports] = useState([]);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [nonprofits, setNonprofits] = useState([]);
  const [loadingNonprofits, setLoadingNonprofits] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formData, setFormData] = useState({
    title: seed?.title || "",
    description: seed?.description || "",
    type: seed
      ? normalizeMissionVisibility(seed.type)
      : MISSION_TYPE_PUBLIC,
    missionCategory: (() => {
      const mc = seed?.missionCategory || "";
      if (!mc) return "";
      const found = MISSION_CATEGORY_OPTIONS.find((o) => o.value === mc);
      return found ? mc : "other";
    })(),
    customMissionCategory:
      seed?.missionCategory &&
      !MISSION_CATEGORY_OPTIONS.find((o) => o.value === seed.missionCategory)
        ? seed.missionCategory
        : "",
    ...(() => {
      const dep = parseLocationFields(
        seed?.departureCity,
        seed?.departureState,
        seed?.departure,
      );
      const arr = parseLocationFields(
        seed?.arrivalCity,
        seed?.arrivalState,
        seed?.arrival,
      );
      return {
        departureCity: dep.city,
        departureState: dep.state,
        arrivalCity: arr.city,
        arrivalState: arr.state,
      };
    })(),
    dateTime: seed?.dateTime || "",
    departureAirportId: seed?.departureAirportId || "",
    arrivalAirportId: seed?.arrivalAirportId || "",
    pilotRequirements: (() => {
      const pr = seed?.pilotRequirements;
      if (!pr || typeof pr !== "object") return defaultPilotRequirements();
      return {
        commercialRating: pr.commercialRating === "Yes" ? "Yes" : "",
        flightHourMinimums:
          pr.flightHourMinimums && String(pr.flightHourMinimums).trim()
            ? String(pr.flightHourMinimums).trim()
            : "None",
        instrumentFlightRating:
          pr.instrumentFlightRating === "Yes" ? "Yes" : "",
        privatePilotsLicense: pr.privatePilotsLicense === "Yes" ? "Yes" : "",
      };
    })(),
    livesImpacted: seed?.livesImpacted ?? "",
    missionPOCName: seed?.missionPOCName || "",
    missionPOCProfileId: seed?.missionPOCProfileId || "",
    missionPOCOrgId: seed?.missionPOCOrgId || "",
    missionPOCPhone: (() => {
      const p = seed?.missionPOCPhone || "";
      if (!p) return "";
      const digits = (p + "").replace(/\D/g, "").slice(0, 10);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6)
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    })(),
    postedBy: seed?.postedBy ?? getPostedByFromToken(idToken),
    cargoItems: seed?.cargoItems?.length
      ? seed.cargoItems.map((c) => ({ ...defaultCargoItem(), ...c }))
      : [defaultCargoItem()],
    cargoStatus:
      normalizeCargoStatusForForm(seed?.cargoStatus) || CARGO_FORM_NEEDED,
    PassengerItem: (() => {
      const source =
        seed?.passengerItem?.length
          ? seed.passengerItem
          : seed?.PassengerItem?.length
            ? seed.PassengerItem
            : [];
      return source.length
        ? source.map((p) => ({ ...defaultPassengerItem(), ...p }))
        : [defaultPassengerItem()];
    })(),
    legs: seed?.legs?.length
      ? seed.legs.map((l) => ({
          ...defaultLeg(),
          legId: l.legId ?? l.LegId ?? "",
          departureAirportId: l.startAirport ?? l.departureAirportId ?? "",
          arrivalAirportId: l.endAirport ?? l.arrivalAirportId ?? "",
          pilotId: l.pilot?.profileId ?? l.pilotId ?? "",
        }))
      : [defaultLeg()],
    status: seed?.status || "",
  });

  const documents = useMissionDocuments(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPan, setBannerPan] = useState({ x: 0.5, y: 0.5 });
  const [existingBanner, setExistingBanner] = useState(() =>
    parseMissionImageRef(seed?.image),
  );
  const [submitError, setSubmitError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const isBusy = loading;

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoadingAirports(true);
    getAirports(idToken)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeAirportsList(res?.data);
        setAirports(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAirports([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAirports(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  // Auto-set postedBy from org when creating (not editing) and token context available
  useEffect(() => {
    if (initialData?.postedBy) return;
    const val = getPostedByFromToken(idToken);
    setFormData((prev) =>
      prev.postedBy !== val ? { ...prev, postedBy: val } : prev,
    );
  }, [idToken, initialData?.postedBy]);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoadingNonprofits(true);
    getNonProfits(idToken)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeNonprofitsList(res?.data);
        setNonprofits(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setNonprofits([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingNonprofits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoadingUsers(true);
    getUsers(idToken)
      .then((res) => {
        if (cancelled) return;
        const list = normalizeUsersList(res?.data);
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  const getAirportId = (airport) =>
    airport?.airportId ?? airport?.entityId ?? airport?.id ?? "";
  const getAirportLabel = (airport) =>
    airport?.name || `Airport ${getAirportId(airport) || "—"}`;

  const getNonprofitId = (np) =>
    np?.nonprofitId ?? np?.entityId ?? np?.id ?? "";
  const getNonprofitLabel = (np) =>
    np?.name || `Organization ${getNonprofitId(np) || "—"}`;

  const getUserId = (u) => u?.profileId ?? u?.id ?? "";
  const getUserLabel = (u) =>
    u?.name
      ? `${u.name}${u.email ? ` (${u.email})` : ""}`
      : `User ${getUserId(u) || "—"}`;

  // Contacts = users associated with the selected POC organization (NonProfit)
  const getOrgContacts = () => {
    const orgId = formData.missionPOCOrgId;
    if (!orgId) return [];
    const associations = (u) =>
      u?.association ?? u?.associations ?? u?.profile?.association ?? [];
    return users.filter((u) => {
      const list = Array.isArray(associations(u)) ? associations(u) : [];
      return list.some(
        (a) =>
          /nonprofit|non.?profit/i.test(String(a?.entityType || "")) &&
          (a?.entityId || "") === orgId,
      );
    });
  };

  const addLeg = () => {
    setFormData({
      ...formData,
      legs: [...(formData.legs || []), defaultLeg()],
    });
  };

  const removeLeg = (index) => {
    if ((formData.legs || []).length <= 1) return;
    setFormData({
      ...formData,
      legs: formData.legs.filter((_, i) => i !== index),
    });
  };

  const handleLegChange = (index, field, value) => {
    const next = [...(formData.legs || [])];
    next[index] = { ...next[index], [field]: value };
    setFormData({ ...formData, legs: next });
  };

  // Phone number validation - US format: (123) 123-1234
  const validatePhoneNumber = (phone) => {
    if (!phone || !phone.trim()) return null;
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "Phone number must be exactly 10 digits in format (123) 123-1234";
    }
    const usPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!usPhoneRegex.test(phone.trim())) {
      return "Phone number must be in format (123) 123-1234";
    }
    return null;
  };

  const formatPhoneNumber = (value) => {
    const digitsOnly = (value || "").replace(/\D/g, "");
    const limitedDigits = digitsOnly.slice(0, 10);
    if (limitedDigits.length <= 3) return limitedDigits;
    if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    }
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(
      3,
      6,
    )}-${limitedDigits.slice(6)}`;
  };

  const handleChange = (field, value) => {
    if (field === "missionPOCPhone") value = formatPhoneNumber(value);
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: "" });
  };

  const handlePilotReqChange = (key, checked) => {
    const pr = {
      ...(formData.pilotRequirements || defaultPilotRequirements()),
    };
    pr[key] = checked ? "Yes" : "No";
    setFormData({ ...formData, pilotRequirements: pr });
  };

  const handleCargoChange = (index, field, value) => {
    const next = [...formData.cargoItems];
    next[index] = { ...next[index], [field]: value };
    setFormData({ ...formData, cargoItems: next });
  };

  const handlePassengerChange = (index, field, value) => {
    const next = [...formData.PassengerItem];
    next[index] = { ...next[index], [field]: value };
    setFormData({ ...formData, PassengerItem: next });
  };

  const addCargoItem = () => {
    setFormData({
      ...formData,
      cargoItems: [...formData.cargoItems, defaultCargoItem()],
    });
  };

  const removeCargoItem = (index) => {
    if (formData.cargoItems.length <= 1) return;
    setFormData({
      ...formData,
      cargoItems: formData.cargoItems.filter((_, i) => i !== index),
    });
  };

  const addPassengerItem = () => {
    setFormData({
      ...formData,
      PassengerItem: [...formData.PassengerItem, defaultPassengerItem()],
    });
  };

  const removePassengerItem = (index) => {
    if (formData.PassengerItem.length <= 1) return;
    setFormData({
      ...formData,
      PassengerItem: formData.PassengerItem.filter((_, i) => i !== index),
    });
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 0 && !(formData.title || "").trim()) {
      newErrors.title = "Title is required";
    }
    if (step === 4) {
      if (formData.missionPOCPhone?.trim()) {
        const phoneError = validatePhoneNumber(formData.missionPOCPhone);
        if (phoneError) newErrors.missionPOCPhone = phoneError;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const buildMissionPayload = (statusOverride) => {
    const missionCategoryValue =
      formData.missionCategory === "other" &&
      formData.customMissionCategory?.trim()
        ? formData.customMissionCategory.trim()
        : formData.missionCategory;
    const departure =
      [formData.departureCity, formData.departureState]
        .filter(Boolean)
        .map((s) => (s || "").trim())
        .join(", ") || undefined;
    const arrival =
      [formData.arrivalCity, formData.arrivalState]
        .filter(Boolean)
        .map((s) => (s || "").trim())
        .join(", ") || undefined;
    const { customMissionCategory: _drop, ...rest } = formData;
    const cargoItems = formData.cargoItems.map((c) => ({
      ...c,
      count: c.count === "" ? undefined : c.count,
      weightLbs: c.weightLbs === "" ? undefined : c.weightLbs,
      sourcedWeightLbs:
        c.sourcedWeightLbs === "" ? undefined : c.sourcedWeightLbs,
    }));
    const legs = formData.legs || [];
    const resolvedStatus =
      statusOverride != null && statusOverride !== ""
        ? statusOverride
        : formData.status ||
          (initialData ? initialData.status : "") ||
          "Active";
    return {
      ...rest,
      status: resolvedStatus,
      departure,
      arrival,
      missionCategory: missionCategoryValue,
      livesImpacted:
        formData.livesImpacted === "" ? undefined : formData.livesImpacted,
      cargoItems,
      pilotStatus: derivePilotStatus(legs),
      cargoStatus: formData.cargoStatus || CARGO_FORM_NEEDED,
      passengerItem: formData.PassengerItem.map((p) => ({
        ...p,
        age: p.age === "" ? undefined : p.age,
        weightLbs: p.weightLbs === "" ? undefined : p.weightLbs,
      })),
      legs,
      pilotRequirements:
        formData.pilotRequirements || defaultPilotRequirements(),
      ...(existingBanner?.fileName && !bannerFile
        ? {
            image: {
              fileName: existingBanner.fileName,
              ...(existingBanner.bucket
                ? { bucket: existingBanner.bucket }
                : {}),
            },
          }
        : {}),
    };
  };

  const validateForSubmit = () => {
    const newErrors = {};
    if (!(formData.title || "").trim()) {
      newErrors.title = "Title is required";
    }
    if (formData.missionPOCPhone?.trim()) {
      const phoneError = validatePhoneNumber(formData.missionPOCPhone);
      if (phoneError) newErrors.missionPOCPhone = phoneError;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitMission = async (statusOverride) => {
    if (!validateForSubmit()) return;
    setSubmitError("");
    setUploadProgress(null);
    try {
      const payload = buildMissionPayload(statusOverride);
      let pendingBannerFile = null;
      if (bannerFile) {
        pendingBannerFile = await cropImageToBannerFile(bannerFile, bannerPan);
      }
      const result = onSubmit(payload, {
        pendingFiles: documents.pendingFiles,
        pendingBannerFile,
        onUploadProgress: (name, percent) =>
          setUploadProgress({ name, percent }),
      });
      if (result && typeof result.then === "function") {
        await result;
      }
      documents.clearPending();
      if (pendingBannerFile) {
        setBannerFile(null);
      }
    } catch (err) {
      setSubmitError(err.message || "Failed to save mission");
    } finally {
      setUploadProgress(null);
    }
  };

  const handleSubmit = () => {
    submitMission();
  };

  const handleSaveDraft = () => {
    submitMission("Draft");
  };

  const isLastStep = activeStep === STEPS.length - 1;

  const initialStatusNorm = (initialData?.status || "")
    .toString()
    .trim()
    .toLowerCase();
  const isEditingDraftMission =
    !!initialData &&
    (initialStatusNorm === "draft" || initialStatusNorm.includes("draft"));
  const showSaveToDraft = !initialData || isEditingDraftMission;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Basic Info
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Title *
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Mission title"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                error={!!errors.title}
              />
              {errors.title && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.title}
                </SoftTypography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Mission Category
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Select category"
                options={MISSION_CATEGORY_OPTIONS}
                value={MISSION_CATEGORY_OPTIONS.find(
                  (o) => o.value === formData.missionCategory,
                )}
                onChange={(opt) => {
                  const newCat = opt?.value ?? "";
                  handleChange("missionCategory", newCat);
                  if (newCat !== "other") {
                    setFormData((prev) => ({
                      ...prev,
                      missionCategory: newCat,
                      customMissionCategory: "",
                    }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Public Visibility
                </SoftTypography>
              </SoftBox>
              <SoftBox
                display="flex"
                alignItems="center"
                gap={1.5}
                minHeight="2.5rem"
              >
                <SoftTypography
                  variant="button"
                  color={
                    formData.type === MISSION_TYPE_INTERNAL
                      ? "dark"
                      : "secondary"
                  }
                  fontWeight={
                    formData.type === MISSION_TYPE_INTERNAL
                      ? "medium"
                      : "regular"
                  }
                >
                  Internal
                </SoftTypography>
                <Switch
                  checked={formData.type === MISSION_TYPE_PUBLIC}
                  onChange={(e) =>
                    handleChange(
                      "type",
                      e.target.checked
                        ? MISSION_TYPE_PUBLIC
                        : MISSION_TYPE_INTERNAL,
                    )
                  }
                  color="info"
                />
                <SoftTypography
                  variant="button"
                  color={
                    formData.type === MISSION_TYPE_PUBLIC ? "dark" : "secondary"
                  }
                  fontWeight={
                    formData.type === MISSION_TYPE_PUBLIC ? "medium" : "regular"
                  }
                >
                  Public
                </SoftTypography>
              </SoftBox>
            </Grid>
            {formData.missionCategory === "other" && (
              <Grid item xs={12} md={6}>
                <SoftBox mb={0.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Specify category
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  placeholder="Enter custom category"
                  value={formData.customMissionCategory}
                  onChange={(e) =>
                    handleChange("customMissionCategory", e.target.value)
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Description
                </SoftTypography>
              </SoftBox>
              <SoftInput
                multiline
                rows={3}
                placeholder="Mission description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <MissionBannerField
                existingImage={existingBanner}
                idToken={idToken}
                file={bannerFile}
                pan={bannerPan}
                onFileChange={(next) => {
                  setBannerFile(next);
                  setBannerPan({ x: 0.5, y: 0.5 });
                  if (next) setExistingBanner(null);
                }}
                onPanChange={setBannerPan}
                onRemoveExisting={() => setExistingBanner(null)}
                disabled={isBusy}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Documents
                </SoftTypography>
              </SoftBox>
              <MissionDocumentsField
                inputId="mission-form-documents-upload"
                linkedDocument={documents.linkedDocument}
                pendingFiles={documents.pendingFiles}
                onAddFiles={documents.addFiles}
                onRemovePending={documents.removePending}
                onRemoveLinked={documents.removeLinked}
                disabled={isBusy}
                error={submitError}
                uploadProgress={uploadProgress}
              />
            </Grid>
          </Grid>
        );

      case 1: // Routing
        return (
          <Grid container spacing={2}>
            {/* Proposed Departure Date/Time */}
            <Grid item xs={12} md={6}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Proposed Departure Date/Time
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="datetime-local"
                value={formData.dateTime}
                onChange={(e) => handleChange("dateTime", e.target.value)}
              />
            </Grid>

            {/* Section 1: Departure & Arrival Location */}
            <Grid item xs={12}>
              <SoftBox
                p={2.5}
                sx={(theme) => ({
                  borderRadius: "1rem",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(99, 110, 255, 0.06)",
                })}
              >
                <SoftBox mb={2}>
                  <SoftTypography
                    variant="button"
                    fontWeight="bold"
                    color="dark"
                    display="block"
                  >
                    Departure & Arrival Location
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="secondary"
                    display="block"
                    mt={0.5}
                  >
                    City and state help find the best departure/arrival points.
                    Use flight legs below to assign start/end airports and
                    pilots per segment.
                  </SoftTypography>
                </SoftBox>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color="secondary"
                      display="block"
                      mb={1}
                    >
                      Departure
                    </SoftTypography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <SoftInput
                          placeholder="City"
                          value={formData.departureCity}
                          onChange={(e) =>
                            handleChange("departureCity", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SoftSelect
                          placeholder="State"
                          options={US_STATES}
                          value={US_STATES.find(
                            (o) => o.value === formData.departureState,
                          )}
                          onChange={(opt) =>
                            handleChange("departureState", opt?.value ?? "")
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display={{ xs: "none", md: "flex" }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon sx={{ color: "text.secondary", fontSize: 28 }}>
                      arrow_forward
                    </Icon>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color="secondary"
                      display="block"
                      mb={1}
                    >
                      Arrival
                    </SoftTypography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <SoftInput
                          placeholder="City"
                          value={formData.arrivalCity}
                          onChange={(e) =>
                            handleChange("arrivalCity", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SoftSelect
                          placeholder="State"
                          options={US_STATES}
                          value={US_STATES.find(
                            (o) => o.value === formData.arrivalState,
                          )}
                          onChange={(opt) =>
                            handleChange("arrivalState", opt?.value ?? "")
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </SoftBox>
            </Grid>

            {/* Mission Legs */}
            <Grid item xs={12}>
              <SoftBox
                p={2.5}
                mt={0.5}
                sx={(theme) => ({
                  borderRadius: "1rem",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(99, 110, 255, 0.06)",
                })}
              >
                <SoftBox mb={2}>
                  <SoftTypography
                    variant="button"
                    fontWeight="bold"
                    color="dark"
                    display="block"
                  >
                    Flight Legs
                  </SoftTypography>
                  <SoftTypography
                    variant="caption"
                    color="secondary"
                    display="block"
                    mt={0.5}
                  >
                    Add flight legs with start airport, end airport, and
                    assigned pilot for each segment.
                  </SoftTypography>
                </SoftBox>
                {(formData.legs || []).map((leg, index) => (
                  <SoftBox
                    key={index}
                    mb={2}
                    p={2}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1.5}
                    >
                      <SoftTypography variant="caption" fontWeight="bold">
                        Leg {index + 1}
                      </SoftTypography>
                      <SoftButton
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => removeLeg(index)}
                        disabled={(formData.legs || []).length <= 1}
                      >
                        Remove
                      </SoftButton>
                    </SoftBox>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={4}>
                        <SoftBox mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Start Airport
                          </SoftTypography>
                        </SoftBox>
                        <Autocomplete
                          options={airports}
                          getOptionLabel={getAirportLabel}
                          value={
                            airports.find(
                              (a) => getAirportId(a) === leg.departureAirportId,
                            ) ?? null
                          }
                          onChange={(_, newValue) =>
                            handleLegChange(
                              index,
                              "departureAirportId",
                              newValue ? getAirportId(newValue) : "",
                            )
                          }
                          loading={loadingAirports}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select airport"
                              size="small"
                              variant="outlined"
                              sx={autocompleteInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingAirports ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SoftBox mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            End Airport
                          </SoftTypography>
                        </SoftBox>
                        <Autocomplete
                          options={airports}
                          getOptionLabel={getAirportLabel}
                          value={
                            airports.find(
                              (a) => getAirportId(a) === leg.arrivalAirportId,
                            ) ?? null
                          }
                          onChange={(_, newValue) =>
                            handleLegChange(
                              index,
                              "arrivalAirportId",
                              newValue ? getAirportId(newValue) : "",
                            )
                          }
                          loading={loadingAirports}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select airport"
                              size="small"
                              variant="outlined"
                              sx={autocompleteInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingAirports ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <SoftBox mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Assigned Pilot
                          </SoftTypography>
                        </SoftBox>
                        <Autocomplete
                          options={users}
                          getOptionLabel={getUserLabel}
                          value={
                            users.find((u) => getUserId(u) === leg.pilotId) ??
                            null
                          }
                          onChange={(_, newValue) =>
                            handleLegChange(
                              index,
                              "pilotId",
                              newValue ? getUserId(newValue) : "",
                            )
                          }
                          loading={loadingUsers}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select pilot"
                              size="small"
                              variant="outlined"
                              sx={autocompleteInputSx}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingUsers ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </SoftBox>
                ))}
                <SoftButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={addLeg}
                  startIcon={<Icon>add</Icon>}
                >
                  Add Leg
                </SoftButton>
              </SoftBox>
            </Grid>
          </Grid>
        );

      case 2: // Pilot Requirements
        const pr = formData.pilotRequirements || defaultPilotRequirements();
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6} lg={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Flight Hour Minimums
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Select hours"
                options={FLIGHT_HOUR_MINIMUM_OPTIONS}
                value={
                  FLIGHT_HOUR_MINIMUM_OPTIONS.find(
                    (o) => o.value === (pr.flightHourMinimums || "None"),
                  ) || FLIGHT_HOUR_MINIMUM_OPTIONS[0]
                }
                onChange={(opt) => {
                  const next = {
                    ...(formData.pilotRequirements ||
                      defaultPilotRequirements()),
                    flightHourMinimums: opt?.value || "None",
                  };
                  setFormData({ ...formData, pilotRequirements: next });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftBox
                display="flex"
                flexDirection="row"
                flexWrap="nowrap"
                alignItems="center"
                gap={{ xs: 1, sm: 2 }}
                sx={{
                  overflowX: "auto",
                  py: 0.25,
                  "& .MuiFormControlLabel-root": {
                    flexShrink: 0,
                    marginRight: 0,
                    marginLeft: 0,
                  },
                }}
              >
                {PILOT_REQUIREMENT_OPTIONS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={pr[key] === "Yes"}
                        onChange={(e) =>
                          handlePilotReqChange(key, e.target.checked)
                        }
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <SoftTypography
                        variant="caption"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {label}
                      </SoftTypography>
                    }
                  />
                ))}
              </SoftBox>
            </Grid>
          </Grid>
        );

      case 3: // Impact & POC
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <SoftTypography variant="button" fontWeight="bold" mb={1}>
                Impact
              </SoftTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Lives Impacted
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="number"
                placeholder="0"
                value={formData.livesImpacted}
                onChange={(e) => handleChange("livesImpacted", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftTypography
                variant="button"
                fontWeight="bold"
                mt={1.5}
                mb={1}
              >
                Point of Contact
              </SoftTypography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  POC Organization
                </SoftTypography>
              </SoftBox>
              <Autocomplete
                options={nonprofits}
                getOptionLabel={getNonprofitLabel}
                value={
                  nonprofits.find(
                    (np) => getNonprofitId(np) === formData.missionPOCOrgId,
                  ) ?? null
                }
                onChange={(_, newValue) => {
                  const orgId = newValue ? getNonprofitId(newValue) : "";
                  setFormData((prev) => ({
                    ...prev,
                    missionPOCOrgId: orgId,
                    // Clear POC when org changes so user picks from new org's contacts
                    missionPOCName: "",
                    missionPOCProfileId: "",
                  }));
                }}
                loading={loadingNonprofits}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select organization"
                    size="small"
                    variant="outlined"
                    sx={autocompleteInputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingNonprofits ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  POC Name
                </SoftTypography>
              </SoftBox>
              <Autocomplete
                options={getOrgContacts()}
                getOptionLabel={getUserLabel}
                value={
                  getOrgContacts().find(
                    (u) => getUserId(u) === formData.missionPOCProfileId,
                  ) ?? null
                }
                onChange={(_, newValue) => {
                  const name = newValue ? (newValue.name || "").trim() : "";
                  const profileId = newValue ? getUserId(newValue) : "";
                  let phone = formData.missionPOCPhone;
                  if (newValue?.phone) {
                    const digits = String(newValue.phone || "")
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    phone =
                      digits.length <= 3
                        ? digits
                        : digits.length <= 6
                        ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                        : `(${digits.slice(0, 3)}) ${digits.slice(
                            3,
                            6,
                          )}-${digits.slice(6)}`;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    missionPOCName: name,
                    missionPOCProfileId: profileId,
                    missionPOCPhone: newValue?.phone
                      ? phone
                      : prev.missionPOCPhone,
                  }));
                }}
                loading={loadingUsers}
                disabled={!formData.missionPOCOrgId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      formData.missionPOCOrgId
                        ? "Select contact"
                        : "Select organization first"
                    }
                    size="small"
                    variant="outlined"
                    sx={autocompleteInputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox mb={0.5}>
                <SoftTypography variant="caption" fontWeight="bold">
                  POC Phone
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="tel"
                placeholder="(123) 123-1234"
                value={formData.missionPOCPhone}
                onChange={(e) =>
                  handleChange("missionPOCPhone", e.target.value)
                }
                error={!!errors.missionPOCPhone}
              />
              {errors.missionPOCPhone && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  mt={0.25}
                  display="block"
                >
                  {errors.missionPOCPhone}
                </SoftTypography>
              )}
            </Grid>
          </Grid>
        );

      case 4: // Cargo Items
        return (
          <SoftBox>
            <SoftBox mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.cargoStatus === CARGO_FORM_SOURCED}
                    onChange={(e) =>
                      handleChange(
                        "cargoStatus",
                        e.target.checked
                          ? CARGO_FORM_SOURCED
                          : CARGO_FORM_NEEDED,
                      )
                    }
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <SoftTypography variant="button" fontWeight="medium">
                    Cargo sourced
                  </SoftTypography>
                }
              />
              <SoftTypography variant="caption" color="text" display="block">
                {formData.cargoStatus === CARGO_FORM_SOURCED
                  ? "Status: Sourced"
                  : "Status: Needed"}
              </SoftTypography>
            </SoftBox>
            {formData.cargoItems.map((item, index) => (
              <SoftBox
                key={index}
                mb={2}
                p={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <SoftTypography variant="caption">
                      Description
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      value={item.description}
                      onChange={(e) =>
                        handleCargoChange(index, "description", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">Count</SoftTypography>
                    <SoftInput
                      type="number"
                      inputProps={{ min: 0 }}
                      size="small"
                      placeholder="e.g. 5"
                      value={item.count}
                      onChange={(e) =>
                        handleCargoChange(index, "count", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">
                      Weight (lbs)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      value={item.weightLbs}
                      onChange={(e) =>
                        handleCargoChange(index, "weightLbs", e.target.value)
                      }
                    />
                  </Grid>
                  {initialData && (
                    <Grid item xs={6} md={2}>
                      <SoftTypography variant="caption">
                        Sourced (lbs)
                      </SoftTypography>
                      <SoftInput
                        type="number"
                        size="small"
                        value={item.sourcedWeightLbs}
                        onChange={(e) =>
                          handleCargoChange(
                            index,
                            "sourcedWeightLbs",
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={3}>
                    <SoftTypography variant="caption">
                      Provided By Org ID
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      value={item.providedByOrgId}
                      onChange={(e) =>
                        handleCargoChange(
                          index,
                          "providedByOrgId",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display="flex"
                    alignItems="flex-end"
                  >
                    <SoftButton
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => removeCargoItem(index)}
                      disabled={formData.cargoItems.length <= 1}
                    >
                      Remove
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            ))}
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addCargoItem}
              startIcon={<Icon>add</Icon>}
            >
              Add Cargo Item
            </SoftButton>
          </SoftBox>
        );

      case 5: // Passengers
        return (
          <SoftBox>
            {formData.PassengerItem.map((item, index) => (
              <SoftBox
                key={index}
                mb={2}
                p={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={3}>
                    <SoftTypography variant="caption">Full Name</SoftTypography>
                    <SoftInput
                      size="small"
                      value={item.fullName}
                      onChange={(e) =>
                        handlePassengerChange(index, "fullName", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <SoftTypography variant="caption">Age</SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      value={item.age}
                      onChange={(e) =>
                        handlePassengerChange(index, "age", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">
                      Weight (lbs)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      value={item.weightLbs}
                      onChange={(e) =>
                        handlePassengerChange(
                          index,
                          "weightLbs",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SoftTypography variant="caption">
                      Special Needs
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      value={item.specialNeeds}
                      onChange={(e) =>
                        handlePassengerChange(
                          index,
                          "specialNeeds",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display="flex"
                    alignItems="flex-end"
                  >
                    <SoftButton
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => removePassengerItem(index)}
                      disabled={formData.PassengerItem.length <= 1}
                    >
                      Remove
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            ))}
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addPassengerItem}
              startIcon={<Icon>add</Icon>}
            >
              Add Passenger
            </SoftButton>
          </SoftBox>
        );

      default:
        return null;
    }
  };

  return (
    <SoftBox>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <SoftBox sx={{ overflow: "visible" }}>{renderStepContent()}</SoftBox>

      <SoftBox mt={2} display="flex" justifyContent="space-between">
        <SoftBox display="flex" gap={2}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </SoftButton>
          {activeStep > 0 && (
            <SoftButton
              variant="outlined"
              color="dark"
              onClick={handleBack}
              disabled={isBusy}
              startIcon={<Icon>arrow_back</Icon>}
            >
              Back
            </SoftButton>
          )}
        </SoftBox>
        <SoftBox
          display="flex"
          gap={2}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          {showSaveToDraft && (
            <SoftButton
              variant="outlined"
              color="secondary"
              onClick={handleSaveDraft}
              disabled={isBusy}
              startIcon={<Icon>drafts</Icon>}
            >
              {isBusy ? "Saving..." : "Save to draft"}
            </SoftButton>
          )}
          {initialData && (
            <SoftButton
              variant="outlined"
              color="info"
              onClick={handleSubmit}
              disabled={isBusy}
              startIcon={
                isBusy ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  <Icon>save</Icon>
                )
              }
            >
              {isBusy ? "Saving..." : "Save and Exit"}
            </SoftButton>
          )}
          {isLastStep ? (
            !initialData && (
              <SoftButton
                variant="gradient"
                color="info"
                onClick={handleSubmit}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: "white" }}
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icon sx={{ mr: 1 }}>save</Icon>
                    Create Mission
                  </>
                )}
              </SoftButton>
            )
          ) : (
            <SoftButton
              variant="gradient"
              color="info"
              onClick={handleNext}
              endIcon={<Icon>arrow_forward</Icon>}
            >
              Next
            </SoftButton>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default MissionForm;
